import React from 'react';
import {
  patchOrderAddress,
  patchOrderLocalizacao,
  resolveOrderAddress,
  searchOrderAddress,
} from '../services/orderAddressService';
import { formatOrderStreetAddress, parseOrderAddressLines } from '../utils/orderDetailsAddressUtils';

const LOCAL_MAX = 100;

export function useOrderDetailsAddressEdit(order, { onAddressUpdated, onLocalizacaoUpdated } = {}) {
  const [addressModalOpen, setAddressModalOpen] = React.useState(false);
  const [addrQuery, setAddrQuery] = React.useState('');
  const [addrHits, setAddrHits] = React.useState([]);
  const [addrSearching, setAddrSearching] = React.useState(false);
  const [selectedDisplay, setSelectedDisplay] = React.useState('');
  const [selectedParsed, setSelectedParsed] = React.useState(null);
  const [isSavingAddress, setIsSavingAddress] = React.useState(false);
  const [addressFeedback, setAddressFeedback] = React.useState(null);

  const [isLocalEditing, setIsLocalEditing] = React.useState(false);
  const [localEditValue, setLocalEditValue] = React.useState('');
  const [isSavingLocal, setIsSavingLocal] = React.useState(false);
  const [localFeedback, setLocalFeedback] = React.useState(null);

  const addrSeqRef = React.useRef(0);
  const addrTimerRef = React.useRef(null);

  const currentStreet = React.useMemo(() => formatOrderStreetAddress(order), [order]);
  const currentLine2 = React.useMemo(() => {
    const lines = parseOrderAddressLines(order);
    return lines.line2 || '';
  }, [order]);
  const currentLocal = React.useMemo(() => {
    return String(order?.TB02115_LOCAL ?? order?.TB02112_LOCAL ?? '').trim();
  }, [order]);

  React.useEffect(() => {
    setAddressModalOpen(false);
    setAddrQuery('');
    setAddrHits([]);
    setSelectedDisplay('');
    setSelectedParsed(null);
    setAddressFeedback(null);
    setIsLocalEditing(false);
    setLocalEditValue('');
    setLocalFeedback(null);
  }, [order?.id]);

  React.useEffect(() => () => {
    clearTimeout(addrTimerRef.current);
  }, []);

  const openAddressModal = React.useCallback(() => {
    setAddrQuery('');
    setAddrHits([]);
    setSelectedDisplay('');
    setSelectedParsed(null);
    setAddressFeedback(null);
    setAddressModalOpen(true);
  }, []);

  const closeAddressModal = React.useCallback(() => {
    if (isSavingAddress) return;
    setAddressModalOpen(false);
    setAddrQuery('');
    setAddrHits([]);
    setSelectedDisplay('');
    setSelectedParsed(null);
  }, [isSavingAddress]);

  const scheduleAddressSearch = React.useCallback((value) => {
    const next = String(value || '');
    setAddrQuery(next);
    setSelectedDisplay('');
    setSelectedParsed(null);
    clearTimeout(addrTimerRef.current);
    const q = next.replace(/\s+/g, ' ').trim();
    if (q.length < 4) {
      setAddrHits([]);
      setAddrSearching(false);
      return;
    }
    setAddrSearching(true);
    addrTimerRef.current = setTimeout(async () => {
      const seq = ++addrSeqRef.current;
      try {
        const hits = await searchOrderAddress(q);
        if (seq !== addrSeqRef.current) return;
        setAddrHits(Array.isArray(hits) ? hits : []);
      } catch {
        if (seq !== addrSeqRef.current) return;
        setAddrHits([]);
      } finally {
        if (seq === addrSeqRef.current) setAddrSearching(false);
      }
    }, 500);
  }, []);

  const pickAddressSuggestion = React.useCallback(async (hit) => {
    let item = hit;
    try {
      item = (await resolveOrderAddress(hit, addrQuery)) || hit;
    } catch {
      item = hit;
    }
    const display = String(item?.display || item?.texto || '').trim();
    if (!display) return;
    setSelectedDisplay(display);
    setSelectedParsed(item?.parsed || null);
    setAddrQuery(display);
    setAddrHits([]);
    setAddrSearching(false);
  }, [addrQuery]);

  const confirmAddress = React.useCallback(async () => {
    if (!order?.id || isSavingAddress || !selectedParsed) return;
    setIsSavingAddress(true);
    setAddressFeedback(null);
    try {
      const data = await patchOrderAddress(order.id, {
        logradouro: selectedParsed.logradouro,
        numero: selectedParsed.numero,
        complemento: selectedParsed.complemento,
        bairro: selectedParsed.bairro,
        cidade: selectedParsed.cidade,
        estado: selectedParsed.estado,
        cep: selectedParsed.cep,
      });
      onAddressUpdated?.(data);
      setAddressFeedback({ type: 'success', message: 'Endereço atualizado.' });
      setAddressModalOpen(false);
      setAddrQuery('');
      setSelectedParsed(null);
      setSelectedDisplay('');
      window.setTimeout(() => setAddressFeedback(null), 2500);
    } catch (err) {
      setAddressFeedback({ type: 'error', message: err.message || 'Erro ao atualizar endereço' });
    } finally {
      setIsSavingAddress(false);
    }
  }, [order?.id, isSavingAddress, selectedParsed, onAddressUpdated]);

  const startLocalEdit = React.useCallback(() => {
    setLocalEditValue(currentLocal);
    setIsLocalEditing(true);
    setLocalFeedback(null);
  }, [currentLocal]);

  const cancelLocalEdit = React.useCallback(() => {
    if (isSavingLocal) return;
    setIsLocalEditing(false);
    setLocalEditValue('');
  }, [isSavingLocal]);

  const saveLocal = React.useCallback(async () => {
    if (!order?.id || isSavingLocal) return;
    const next = String(localEditValue || '').trim().slice(0, LOCAL_MAX);
    if (next === currentLocal) {
      setIsLocalEditing(false);
      return;
    }
    setIsSavingLocal(true);
    setLocalFeedback(null);
    try {
      const data = await patchOrderLocalizacao(order.id, next);
      onLocalizacaoUpdated?.(data?.localizacao ?? next);
      setIsLocalEditing(false);
      setLocalFeedback({ type: 'success', message: 'Localização atualizada.' });
      window.setTimeout(() => setLocalFeedback(null), 2500);
    } catch (err) {
      setLocalFeedback({ type: 'error', message: err.message || 'Erro ao atualizar localização' });
    } finally {
      setIsSavingLocal(false);
    }
  }, [order?.id, isSavingLocal, localEditValue, currentLocal, onLocalizacaoUpdated]);

  return {
    addressModalOpen,
    openAddressModal,
    closeAddressModal,
    currentStreet,
    currentLine2,
    addrQuery,
    scheduleAddressSearch,
    addrHits,
    addrSearching,
    selectedDisplay,
    selectedParsed,
    pickAddressSuggestion,
    confirmAddress,
    isSavingAddress,
    addressFeedback,
    canConfirmAddress: Boolean(selectedParsed) && !isSavingAddress,

    currentLocal,
    isLocalEditing,
    localEditValue,
    setLocalEditValue,
    isSavingLocal,
    localFeedback,
    startLocalEdit,
    cancelLocalEdit,
    saveLocal,
    LOCAL_MAX,
  };
}
